import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, functions, storageInstance } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';

export default function ManageRaffle() {
  const { id: raffleId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [raffle, setRaffle] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');
  const [viewingComprovante, setViewingComprovante] = useState(null);
  // Winner selection
  const [showWinnerModal, setShowWinnerModal] = useState(false);
  const [selectedWinnerNumber, setSelectedWinnerNumber] = useState('');
  const [drawProofFile, setDrawProofFile] = useState(null);
  const [drawProofPreview, setDrawProofPreview] = useState(null);
  const [showWinnerConfirm, setShowWinnerConfirm] = useState(false);
  const [winnerLoading, setWinnerLoading] = useState(false);
  const [winnerTicketInfo, setWinnerTicketInfo] = useState(null);

  // Rejection modal
  const [rejectingPurchase, setRejectingPurchase] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // Manual assignment modal
  const [showManualAssign, setShowManualAssign] = useState(false);
  const [manualNumbers, setManualNumbers] = useState('');
  const [manualBuyerName, setManualBuyerName] = useState('');
  const [manualBuyerEmail, setManualBuyerEmail] = useState('');
  const [manualJustification, setManualJustification] = useState('');
  const [manualComprovante, setManualComprovante] = useState(null);
  const [manualComprovantePreview, setManualComprovantePreview] = useState(null);
  const [manualLoading, setManualLoading] = useState(false);

  // Remove numbers modal
  const [showRemoveNumbers, setShowRemoveNumbers] = useState(false);
  const [removeTicket, setRemoveTicket] = useState(null); // ticket to remove
  const [removeJustification, setRemoveJustification] = useState('');
  const [removeLoading, setRemoveLoading] = useState(false);

  // Delete raffle modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Migration
  const [migrateLoading, setMigrateLoading] = useState(false);

  // Edit raffle
  const [showEditSection, setShowEditSection] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editMediaFiles, setEditMediaFiles] = useState([]); // new files to upload
  const [existingMedia, setExistingMedia] = useState([]); // already uploaded media
  const [editSaving, setEditSaving] = useState(false);
  const editMediaInputRef = useRef(null);

  const fetchData = async () => {
    if (!currentUser) return;

    try {
      // Fetch raffle
      const raffleDoc = await getDoc(doc(db, 'raffles', raffleId));
      if (!raffleDoc.exists()) {
        navigate('/my-raffles');
        return;
      }

      const raffleData = { id: raffleDoc.id, ...raffleDoc.data() };

      // Only creator can manage
      if (raffleData.createdBy !== currentUser.uid) {
        navigate('/my-raffles');
        return;
      }

      setRaffle(raffleData);

      // Fetch all purchases for this raffle
      // Security rules require raffleCreatorId == auth.uid for reads,
      // so we must include that filter for the query to be allowed by Firestore
      let purchasesList;
      try {
        const purchasesQuery = query(
          collection(db, 'purchases'),
          where('raffleId', '==', raffleId),
          where('raffleCreatorId', '==', currentUser.uid),
          orderBy('createdAt', 'desc')
        );
        const purchasesSnap = await getDocs(purchasesQuery);
        purchasesList = purchasesSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (indexError) {
        console.warn('Composite index not ready, using fallback query:', indexError.message);
        const fallbackQuery = query(
          collection(db, 'purchases'),
          where('raffleId', '==', raffleId),
          where('raffleCreatorId', '==', currentUser.uid)
        );
        const fallbackSnap = await getDocs(fallbackQuery);
        purchasesList = fallbackSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        purchasesList.sort((a, b) => {
          const aTime = a.createdAt?.toMillis?.() || 0;
          const bTime = b.createdAt?.toMillis?.() || 0;
          return bTime - aTime;
        });
      }
      setPurchases(purchasesList);

      // Fetch all tickets
      const ticketsSnap = await getDocs(collection(db, 'raffles', raffleId, 'tickets'));
      const ticketsList = ticketsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTickets(ticketsList);

    } catch (error) {
      console.error('Error fetching manage data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, raffleId]);

  // ─── Migrate to 0-based ───
  const handleMigrateZeroBased = async () => {
    if (!window.confirm('Migrar para 0-based? O grid passará a mostrar de 0 a N-1 (ex: 0-999). Números já vendidos não mudam de dono.')) return;
    setMigrateLoading(true);
    try {
      const migrate = httpsCallable(functions, 'migrate_raffle_to_zero_based');
      const result = await migrate({ raffleId });
      alert(result.data.message);
      window.location.reload();
    } catch (err) {
      alert('Erro na migração: ' + (err.message || err));
    } finally {
      setMigrateLoading(false);
    }
  };

  // ─── Edit Raffle Handlers ───
  const openEditSection = () => {
    if (!raffle) return;
    setEditTitle(raffle.title || '');
    setEditDescription(raffle.description || '');
    setExistingMedia(raffle.mediaGallery || (raffle.imageUrl ? [{ url: raffle.imageUrl, type: 'image' }] : []));
    setEditMediaFiles([]);
    setShowEditSection(true);
  };

  const handleEditMediaAdd = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    const totalCount = existingMedia.length + editMediaFiles.length;
    const remaining = 3 - totalCount;
    if (remaining <= 0) { alert('Máximo de 3 mídias.'); return; }
    const toAdd = files.slice(0, remaining).map(file => ({
      file,
      preview: URL.createObjectURL(file),
      type: file.type.startsWith('video/') ? 'video' : 'image',
    }));
    setEditMediaFiles(prev => [...prev, ...toAdd]);
    if (editMediaInputRef.current) editMediaInputRef.current.value = '';
  };

  const handleRemoveExistingMedia = (index) => {
    setExistingMedia(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveNewMedia = (index) => {
    setEditMediaFiles(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) { alert('Título é obrigatório.'); return; }
    setEditSaving(true);
    try {
      // Upload new media files
      const newMediaUrls = [];
      for (let i = 0; i < editMediaFiles.length; i++) {
        const media = editMediaFiles[i];
        const ext = media.file.name.split('.').pop();
        const fileName = `edit_${i}_${Date.now()}.${ext}`;
        const mediaRef = ref(storageInstance, `raffles/${raffleId}/${fileName}`);
        const snap = await uploadBytes(mediaRef, media.file);
        const url = await getDownloadURL(snap.ref);
        newMediaUrls.push({ url, type: media.type, name: media.file.name });
      }

      const allMedia = [...existingMedia, ...newMediaUrls];
      const firstImage = allMedia.find(m => m.type === 'image');

      await updateDoc(doc(db, 'raffles', raffleId), {
        title: editTitle.trim(),
        description: editDescription.trim(),
        mediaGallery: allMedia,
        imageUrl: firstImage ? firstImage.url : '',
      });

      setShowEditSection(false);
      await fetchData();
    } catch (err) {
      console.error('Error saving edit:', err);
      alert('Erro ao salvar alterações.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleConfirm = async (purchaseId) => {
    if (!window.confirm('Confirmar pagamento? Os números serão confirmados.')) return;
    setActionLoading(purchaseId);
    try {
      const confirmFn = httpsCallable(functions, 'confirm_payment');
      await confirmFn({ purchaseId });
      await fetchData();
    } catch (error) {
      console.error('Error confirming:', error);
      alert(error.message || 'Erro ao confirmar pagamento.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectConfirm = async () => {
    if (!rejectingPurchase) return;
    if (!rejectReason.trim()) {
      alert('Por favor, informe o motivo da rejeição.');
      return;
    }
    setActionLoading(rejectingPurchase);
    try {
      const rejectFn = httpsCallable(functions, 'reject_payment');
      await rejectFn({ purchaseId: rejectingPurchase, reason: rejectReason.trim() });
      setRejectingPurchase(null);
      setRejectReason('');
      await fetchData();
    } catch (error) {
      console.error('Error rejecting:', error);
      alert(error.message || 'Erro ao rejeitar pagamento.');
    } finally {
      setActionLoading(null);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleManualComprovanteChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Arquivo muito grande. Máximo 5MB.');
        return;
      }
      setManualComprovante(file);
      const reader = new FileReader();
      reader.onloadend = () => setManualComprovantePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleManualAssign = async () => {
    if (!manualBuyerName.trim() || !manualBuyerEmail.trim() || !manualNumbers.trim() || !manualJustification.trim()) {
      alert('Preencha todos os campos obrigatórios.');
      return;
    }

    const numbers = manualNumbers.split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n) && n > 0);
    if (numbers.length === 0) {
      alert('Informe números válidos separados por vírgula.');
      return;
    }

    setManualLoading(true);
    try {
      const payload = {
        raffleId,
        numbers,
        buyerName: manualBuyerName.trim(),
        buyerEmail: manualBuyerEmail.trim(),
        justification: manualJustification.trim(),
      };

      // Optional comprovante
      if (manualComprovante) {
        payload.comprovanteBase64 = await fileToBase64(manualComprovante);
        payload.comprovanteFilename = manualComprovante.name;
      }

      const assignFn = httpsCallable(functions, 'manual_assign_numbers');
      const result = await assignFn(payload);

      setShowManualAssign(false);
      setManualNumbers('');
      setManualBuyerName('');
      setManualBuyerEmail('');
      setManualJustification('');
      setManualComprovante(null);
      setManualComprovantePreview(null);
      await fetchData();
      alert(result.data.message || 'Números atribuídos com sucesso!');
    } catch (error) {
      console.error('Error assigning:', error);
      alert(error.message || 'Erro ao atribuir números.');
    } finally {
      setManualLoading(false);
    }
  };

  const handleRemoveNumber = async () => {
    if (!removeTicket) return;
    if (!removeJustification.trim()) {
      alert('Por favor, informe o motivo da remoção.');
      return;
    }

    setRemoveLoading(true);
    try {
      const removeFn = httpsCallable(functions, 'manual_remove_numbers');
      await removeFn({
        raffleId,
        numbers: [removeTicket.number],
        justification: removeJustification.trim(),
      });
      setShowRemoveNumbers(false);
      setRemoveTicket(null);
      setRemoveJustification('');
      await fetchData();
      alert('Número removido com sucesso!');
    } catch (error) {
      console.error('Error removing:', error);
      alert(error.message || 'Erro ao remover número.');
    } finally {
      setRemoveLoading(false);
    }
  };

  const handleDrawProofChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { alert('Arquivo muito grande. Máximo 5MB.'); return; }
      setDrawProofFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setDrawProofPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSelectWinner = () => {
    const num = parseInt(selectedWinnerNumber);
    if (isNaN(num) || num < 0) { alert('Selecione um número válido.'); return; }

    // Find ticket info for the selected number
    const ticket = tickets.find(t => t.number === num && t.status === 'confirmed');
    if (!ticket) { alert('Este número não está entre os bilhetes confirmados.'); return; }

    setWinnerTicketInfo(ticket);
    setShowWinnerConfirm(true);
  };

  const handleConfirmWinner = async () => {
    setWinnerLoading(true);
    try {
      const payload = {
        raffleId,
        winnerNumber: parseInt(selectedWinnerNumber),
      };

      if (drawProofFile) {
        payload.drawProofBase64 = await fileToBase64(drawProofFile);
        payload.drawProofFilename = drawProofFile.name;
      }

      const setWinnerFn = httpsCallable(functions, 'set_winner');
      const result = await setWinnerFn(payload);

      setShowWinnerModal(false);
      setShowWinnerConfirm(false);
      setSelectedWinnerNumber('');
      setDrawProofFile(null);
      setDrawProofPreview(null);
      setWinnerTicketInfo(null);
      await fetchData();
      alert(result.data.message);
    } catch (error) {
      console.error('Error setting winner:', error);
      alert(error.message || 'Erro ao definir vencedor.');
    } finally {
      setWinnerLoading(false);
    }
  };

  const handleDeleteRaffle = async () => {
    setDeleteLoading(true);
    try {
      const deleteRaffleFn = httpsCallable(functions, 'delete_raffle');
      await deleteRaffleFn({ raffleId });
      navigate('/my-raffles');
    } catch (error) {
      console.error('Error deleting raffle:', error);
      alert(error.message || 'Erro ao excluir rifa.');
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', fontSize: '1.5rem' }}>
        Carregando...
      </div>
    );
  }

  if (!raffle) return null;

  const pendingPurchases = purchases.filter(p => p.status === 'pending');
  const confirmedPurchases = purchases.filter(p => p.status === 'confirmed');
  const rejectedPurchases = purchases.filter(p => p.status === 'rejected');
  const confirmedTickets = tickets.filter(t => t.status === 'confirmed');
  const pendingTickets = tickets.filter(t => t.status === 'pending');

  const cur = raffle.currency || 'BRL';
  const totalConfirmed = confirmedPurchases.reduce((sum, p) => sum + (p.totalAmount || 0), 0);
  const totalPending = pendingPurchases.reduce((sum, p) => sum + (p.totalAmount || 0), 0);

  // Check if draw date has passed
  const isExpired = raffle.drawDate && new Date(raffle.drawDate) < new Date();
  const isAwaitingDraw = isExpired && raffle.status === 'active' && !raffle.winnerNumber;
  const canDelete = purchases.length === 0;

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '2rem 1rem' }}>
      {/* Back + Title */}
      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
          <Link to="/my-raffles" style={{
            background: 'var(--gray-100)', borderRadius: '50%', width: '40px', height: '40px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', fontSize: '1.2rem',
            flexShrink: 0,
          }}>
            ←
          </Link>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Gerenciar: {raffle.title}</h1>
            <p style={{ margin: '0.25rem 0 0', color: 'var(--dark-light)', fontSize: '0.9rem' }}>
              {raffle.emoji || '🎰'}{' '}
              {raffle.status === 'completed' ? '✅ Concluída'
                : isAwaitingDraw ? '⏳ Aguardando Sorteio'
                : raffle.status === 'active' ? '🟢 Ativa'
                : raffle.status}
            </p>
          </div>
        </div>

        {/* Action buttons — own row, aligned right */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button
            onClick={openEditSection}
            className="btn btn-sm"
            style={{
              background: 'linear-gradient(135deg, #6C5CE7, #00CEC9)',
              color: 'white', border: 'none', fontWeight: 600,
              padding: '0.5rem 1rem', borderRadius: '20px', cursor: 'pointer',
            }}
          >
            ✏️ Editar
          </button>
          {!raffle.zeroBased && raffle.numberType === 'limited' && (
            <button
              onClick={handleMigrateZeroBased}
              className="btn btn-sm"
              disabled={migrateLoading}
              style={{
                background: 'linear-gradient(135deg, #e84393, #fd79a8)',
                color: 'white', border: 'none', fontWeight: 600,
                padding: '0.5rem 1rem', borderRadius: '20px', cursor: 'pointer',
              }}
            >
              {migrateLoading ? 'Migrando...' : '🔄 Migrar 0-based'}
            </button>
          )}
          {raffle.status === 'active' && (
            <button
              onClick={() => setShowManualAssign(true)}
              className="btn btn-sm"
              style={{
                background: 'linear-gradient(135deg, #fdcb6e, #e17055)',
                color: 'white', border: 'none', fontWeight: 600,
                padding: '0.5rem 1rem', borderRadius: '20px', cursor: 'pointer',
              }}
            >
              ➕ Atribuir Números
            </button>
          )}
          <Link to={raffle.slug ? `/rifa/${raffle.slug}` : `/raffle/${raffleId}`} className="btn btn-primary btn-sm" style={{ textDecoration: 'none' }}>
            Ver Rifa
          </Link>
          {canDelete && (
            <button
              onClick={() => setShowDeleteModal(true)}
              className="btn btn-sm"
              style={{
                background: '#d63031', color: 'white', border: 'none', fontWeight: 600,
                padding: '0.5rem 1rem', borderRadius: '20px', cursor: 'pointer',
              }}
            >
              🗑️ Excluir
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: '1.2rem', boxShadow: 'var(--shadow-md)', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--primary)' }}>{confirmedTickets.length}</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--dark-light)' }}>Confirmados</div>
        </div>
        <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: '1.2rem', boxShadow: 'var(--shadow-md)', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#f39c12' }}>{pendingTickets.length}</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--dark-light)' }}>Pendentes</div>
        </div>
        <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: '1.2rem', boxShadow: 'var(--shadow-md)', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--success)' }}>{formatCurrency(totalConfirmed, cur)}</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--dark-light)' }}>Receita Confirmada</div>
        </div>
        <div style={{ background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: '1.2rem', boxShadow: 'var(--shadow-md)', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: '#f39c12' }}>{formatCurrency(totalPending, cur)}</div>
          <div style={{ fontSize: '0.85rem', color: 'var(--dark-light)' }}>Receita Pendente</div>
        </div>
      </div>

      {/* ─── Edit Section ─── */}
      {showEditSection && (
        <div style={{
          background: 'var(--white)', borderRadius: 'var(--radius-lg)',
          padding: '1.5rem', marginBottom: '2rem', boxShadow: 'var(--shadow-md)',
          border: '2px solid var(--primary)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
            <h2 style={{ margin: 0, fontSize: '1.2rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>✏️</span> Editar Rifa
            </h2>
            <button onClick={() => setShowEditSection(false)} style={{
              background: 'var(--gray-100)', border: 'none', borderRadius: '50%',
              width: '32px', height: '32px', cursor: 'pointer', fontSize: '1rem', fontWeight: 700,
            }}>✕</button>
          </div>

          {/* Title */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.9rem' }}>Título</label>
            <input
              type="text" className="form-control"
              value={editTitle} onChange={e => setEditTitle(e.target.value)}
              style={{ width: '100%', padding: '0.75rem', fontSize: '1rem', border: '2px solid var(--gray-300)', borderRadius: 'var(--radius-sm)' }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.9rem' }}>Descrição</label>
            <textarea
              className="form-control"
              value={editDescription} onChange={e => setEditDescription(e.target.value)}
              rows={5}
              style={{ width: '100%', padding: '0.75rem', fontSize: '0.95rem', border: '2px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', resize: 'vertical' }}
            />
            <small style={{ color: 'var(--dark-light)', fontSize: '0.8rem' }}>
              Suporta **negrito**, *itálico*, [links](url) e listas com markdown
            </small>
          </div>

          {/* Media Gallery */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.9rem' }}>Fotos e Vídeos (máx 3)</label>

            {/* Existing media */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
              {existingMedia.map((item, i) => (
                <div key={`existing-${i}`} style={{
                  width: '100px', height: '100px', borderRadius: 'var(--radius-sm)',
                  overflow: 'hidden', position: 'relative', border: '2px solid var(--gray-300)',
                }}>
                  {item.type === 'video' ? (
                    <video src={item.url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline preload="metadata" />
                  ) : (
                    <img src={item.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                  <button onClick={() => handleRemoveExistingMedia(i)} style={{
                    position: 'absolute', top: '4px', right: '4px',
                    background: 'var(--danger)', color: 'white', border: 'none',
                    borderRadius: '50%', width: '22px', height: '22px',
                    cursor: 'pointer', fontWeight: 700, fontSize: '0.7rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>✕</button>
                  {item.type === 'video' && (
                    <div style={{
                      position: 'absolute', bottom: '4px', left: '4px',
                      background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '0.65rem',
                      padding: '2px 6px', borderRadius: '8px',
                    }}>▶ Vídeo</div>
                  )}
                </div>
              ))}

              {/* New media previews */}
              {editMediaFiles.map((item, i) => (
                <div key={`new-${i}`} style={{
                  width: '100px', height: '100px', borderRadius: 'var(--radius-sm)',
                  overflow: 'hidden', position: 'relative', border: '2px dashed var(--primary)',
                }}>
                  {item.type === 'video' ? (
                    <video src={item.preview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} muted playsInline />
                  ) : (
                    <img src={item.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  )}
                  <button onClick={() => handleRemoveNewMedia(i)} style={{
                    position: 'absolute', top: '4px', right: '4px',
                    background: 'var(--danger)', color: 'white', border: 'none',
                    borderRadius: '50%', width: '22px', height: '22px',
                    cursor: 'pointer', fontWeight: 700, fontSize: '0.7rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>✕</button>
                  <div style={{
                    position: 'absolute', bottom: '4px', left: '4px',
                    background: 'rgba(108,92,231,0.8)', color: '#fff', fontSize: '0.6rem',
                    padding: '2px 6px', borderRadius: '8px',
                  }}>Novo</div>
                </div>
              ))}

              {/* Add button */}
              {(existingMedia.length + editMediaFiles.length) < 3 && (
                <div
                  onClick={() => editMediaInputRef.current?.click()}
                  style={{
                    width: '100px', height: '100px', borderRadius: 'var(--radius-sm)',
                    border: '2px dashed var(--gray-300)', display: 'flex',
                    flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', color: 'var(--dark-light)', fontSize: '0.8rem',
                    transition: 'all 0.2s', background: 'var(--gray-100)',
                  }}
                >
                  <span style={{ fontSize: '1.5rem', marginBottom: '0.2rem' }}>+</span>
                  Adicionar
                  <input
                    ref={editMediaInputRef}
                    type="file" accept="image/*,video/mp4,video/webm,video/quicktime"
                    onChange={handleEditMediaAdd}
                    multiple
                    style={{ display: 'none' }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Save / Cancel */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setShowEditSection(false)}
              className="btn btn-sm"
              style={{ padding: '0.6rem 1.5rem', borderRadius: 'var(--radius-sm)', fontSize: '0.9rem' }}
              disabled={editSaving}
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveEdit}
              className="btn btn-primary btn-sm"
              style={{
                padding: '0.6rem 1.5rem', borderRadius: 'var(--radius-sm)', fontSize: '0.9rem',
                fontWeight: 700, opacity: editSaving ? 0.6 : 1,
              }}
              disabled={editSaving}
            >
              {editSaving ? '⏳ Salvando...' : '💾 Salvar Alterações'}
            </button>
          </div>
        </div>
      )}

      {/* Winner Banner */}
      {raffle.status === 'completed' && raffle.winnerNumber ? (
        <div style={{
          background: 'linear-gradient(135deg, #f6d365, #fda085)',
          borderRadius: 'var(--radius-lg)',
          padding: '2rem',
          textAlign: 'center',
          marginBottom: '2rem',
          color: '#fff',
        }}>
          <div style={{ fontSize: '3rem' }}>🏆</div>
          <h3 style={{ margin: '0.5rem 0', textAlign: 'center' }}>Vencedor: Número {raffle.winnerNumber}</h3>
          <p style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: 600, textAlign: 'center' }}>{raffle.winnerName}</p>
          {raffle.drawProofUrl && (
            <a href={raffle.drawProofUrl} target="_blank" rel="noopener noreferrer"
              style={{ color: '#fff', fontSize: '0.9rem', textDecoration: 'underline', opacity: 0.9 }}>
              📎 Ver Comprovante do Sorteio
            </a>
          )}
        </div>
      ) : null}

      {/* Awaiting Draw Banner */}
      {isAwaitingDraw && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(253,203,110,0.15), rgba(225,112,85,0.15))',
          borderRadius: 'var(--radius-lg)',
          padding: '1.5rem',
          textAlign: 'center',
          marginBottom: '2rem',
          border: '2px solid #fdcb6e',
        }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⏳</div>
          <h3 style={{ margin: '0 0 0.5rem', color: 'var(--dark)', textAlign: 'center' }}>Período encerrado — Aguardando Sorteio</h3>
          <p style={{ fontSize: '0.9rem', color: 'var(--dark-light)', margin: '0 0 1rem', textAlign: 'center' }}>
            O período de vendas encerrou. Selecione o número vencedor para finalizar a rifa.
            <br/><strong>{confirmedTickets.length}</strong> bilhetes confirmados participam do sorteio.
          </p>
          <button
            onClick={() => { setShowWinnerModal(true); setSelectedWinnerNumber(''); setDrawProofFile(null); setDrawProofPreview(null); }}
            className="btn btn-lg"
            style={{
              background: 'linear-gradient(135deg, #fdcb6e, #e17055)',
              color: 'white', border: 'none', fontWeight: 700, fontSize: '1.1rem',
              padding: '0.8rem 2rem', borderRadius: '25px', cursor: 'pointer',
            }}
          >
            🏆 Definir Vencedor
          </button>
        </div>
      )}

      {/* Active raffle with confirmed tickets — can set winner early too */}
      {raffle.status === 'active' && !isAwaitingDraw && confirmedTickets.length > 0 && (
        <div style={{
          background: 'rgba(108, 92, 231, 0.06)',
          borderRadius: 'var(--radius-lg)',
          padding: '1rem 1.5rem',
          marginBottom: '2rem',
          border: '1px solid rgba(108, 92, 231, 0.15)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem',
        }}>
          <div>
            <span style={{ fontWeight: 600, color: 'var(--dark)' }}>🎯 {confirmedTickets.length} bilhetes confirmados</span>
            <span style={{ color: 'var(--dark-light)', fontSize: '0.9rem', marginLeft: '0.5rem' }}>
              — Sorteio em {new Date(raffle.drawDate).toLocaleDateString('pt-BR')}{raffle.drawDate && raffle.drawDate.includes('T') ? ` às ${new Date(raffle.drawDate).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}
            </span>
          </div>
          <button
            onClick={() => { setShowWinnerModal(true); setSelectedWinnerNumber(''); setDrawProofFile(null); setDrawProofPreview(null); }}
            style={{
              background: 'linear-gradient(135deg, #fdcb6e, #e17055)',
              color: 'white', border: 'none', fontWeight: 600, fontSize: '0.9rem',
              padding: '0.5rem 1.2rem', borderRadius: '20px', cursor: 'pointer',
            }}
          >
            🏆 Definir Vencedor
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex', gap: '0.5rem', marginBottom: '1.5rem',
        borderBottom: '2px solid var(--gray-200)', paddingBottom: '0',
        overflowX: 'auto',
      }}>
        {[
          { key: 'pending', label: 'Pendentes', count: pendingPurchases.length, color: '#f39c12' },
          { key: 'confirmed', label: 'Confirmados', count: confirmedPurchases.length, color: 'var(--success)' },
          { key: 'rejected', label: 'Rejeitados', count: rejectedPurchases.length, color: 'var(--danger)' },
          { key: 'numbers', label: 'Números', count: tickets.length, color: 'var(--primary)' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '0.75rem 1.2rem',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.95rem',
              color: activeTab === tab.key ? 'var(--primary)' : 'var(--dark-light)',
              borderBottom: activeTab === tab.key ? '3px solid var(--primary)' : '3px solid transparent',
              whiteSpace: 'nowrap',
              position: 'relative',
            }}
          >
            {tab.label}
            {tab.count > 0 && (
              <span style={{
                marginLeft: '0.4rem',
                background: tab.key === 'pending' ? tab.color : 'var(--gray-200)',
                color: tab.key === 'pending' ? 'white' : 'var(--dark)',
                borderRadius: '10px',
                padding: '2px 8px',
                fontSize: '0.75rem',
                fontWeight: 700,
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'pending' && (
        pendingPurchases.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {pendingPurchases.map(purchase => (
              <PurchaseCard
                key={purchase.id}
                purchase={purchase}
                currency={cur}
                actionLoading={actionLoading}
                onConfirm={() => handleConfirm(purchase.id)}
                onReject={() => { setRejectingPurchase(purchase.id); setRejectReason(''); }}
                onViewComprovante={() => setViewingComprovante(purchase.comprovanteUrl)}
                showActions
              />
            ))}
          </div>
        ) : (
          <EmptyState icon="✅" title="Nenhum pagamento pendente" subtitle="Todos os pagamentos foram processados." />
        )
      )}

      {activeTab === 'confirmed' && (
        confirmedPurchases.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {confirmedPurchases.map(purchase => (
              <PurchaseCard
                key={purchase.id}
                purchase={purchase}
                currency={cur}
                onViewComprovante={() => setViewingComprovante(purchase.comprovanteUrl)}
              />
            ))}
          </div>
        ) : (
          <EmptyState icon="📭" title="Nenhum pagamento confirmado ainda" subtitle="Confirme os pagamentos pendentes para vê-los aqui." />
        )
      )}

      {activeTab === 'rejected' && (
        rejectedPurchases.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {rejectedPurchases.map(purchase => (
              <PurchaseCard
                key={purchase.id}
                purchase={purchase}
                currency={cur}
                onViewComprovante={() => setViewingComprovante(purchase.comprovanteUrl)}
              />
            ))}
          </div>
        ) : (
          <EmptyState icon="🗑️" title="Nenhum pagamento rejeitado" subtitle="" />
        )
      )}

      {activeTab === 'numbers' && (
        <div>
          <div style={{
            background: 'var(--white)', borderRadius: 'var(--radius-lg)', padding: '1.5rem',
            boxShadow: 'var(--shadow-md)', overflowX: 'auto'
          }}>
            <h3 style={{ margin: '0 0 1rem' }}>Todos os Números</h3>
            {tickets.length > 0 ? (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--gray-200)' }}>
                    <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', color: 'var(--dark-light)' }}>Número</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', color: 'var(--dark-light)' }}>Comprador</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', color: 'var(--dark-light)' }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '0.75rem 0.5rem', color: 'var(--dark-light)' }}>Status</th>
                    <th style={{ textAlign: 'center', padding: '0.75rem 0.5rem', color: 'var(--dark-light)' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets
                    .sort((a, b) => (a.number || 0) - (b.number || 0))
                    .map(ticket => (
                      <tr key={ticket.id} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                        <td style={{ padding: '0.75rem 0.5rem' }}>
                          <span style={{
                            display: 'inline-block', background: 'var(--primary)', color: 'white',
                            padding: '4px 10px', borderRadius: '8px', fontWeight: 700, minWidth: '30px', textAlign: 'center'
                          }}>
                            {ticket.number}
                          </span>
                        </td>
                        <td style={{ padding: '0.75rem 0.5rem' }}>{ticket.buyerName || '-'}</td>
                        <td style={{ padding: '0.75rem 0.5rem', color: 'var(--dark-light)' }}>{ticket.buyerEmail || '-'}</td>
                        <td style={{ padding: '0.75rem 0.5rem' }}>
                          <StatusBadge status={ticket.status} />
                        </td>
                        <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center' }}>
                          <button
                            onClick={() => {
                              setRemoveTicket(ticket);
                              setRemoveJustification('');
                              setShowRemoveNumbers(true);
                            }}
                            title="Remover número"
                            style={{
                              background: 'rgba(214, 48, 49, 0.08)',
                              border: '1px solid rgba(214, 48, 49, 0.3)',
                              borderRadius: '6px',
                              padding: '4px 10px',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              color: '#d63031',
                              fontWeight: 600,
                            }}
                          >
                            🗑️ Remover
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            ) : (
              <p style={{ color: 'var(--dark-light)', textAlign: 'center', padding: '2rem 0' }}>
                Nenhum número vendido ainda.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Comprovante Modal */}
      {viewingComprovante && (
        <div className="modal-overlay" onClick={() => setViewingComprovante(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', textAlign: 'center' }}>
            <h2>📎 Comprovante de Pagamento</h2>
            <img
              src={viewingComprovante}
              alt="Comprovante"
              style={{
                width: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
                borderRadius: 'var(--radius-md)',
                marginBottom: '1rem',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML += '<p style="color: var(--dark-light);">Não foi possível carregar a imagem. <a href="' + viewingComprovante + '" target="_blank" style="color: var(--primary);">Abrir em nova aba</a></p>';
              }}
            />
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <a href={viewingComprovante} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm" style={{ textDecoration: 'none' }}>
                Abrir em Nova Aba
              </a>
              <button className="btn btn-secondary btn-sm" onClick={() => setViewingComprovante(null)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal — with reason field */}
      {rejectingPurchase && (
        <div className="modal-overlay" onClick={() => setRejectingPurchase(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>❌</span> Rejeitar Pagamento
            </h2>

            <div style={{
              background: 'rgba(214, 48, 49, 0.08)',
              border: '1px solid rgba(214, 48, 49, 0.2)',
              borderRadius: 'var(--radius-sm)',
              padding: '1rem',
              marginBottom: '1rem',
              fontSize: '0.9rem',
              color: '#d63031',
            }}>
              Ao rejeitar, os números serão liberados e o comprador receberá um e-mail com o motivo da rejeição e seu email para contato.
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.95rem' }}>
                📝 Motivo da Rejeição *
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Ex: Comprovante ilegível, valor incorreto, comprovante de outra transação..."
                rows={3}
                style={{
                  width: '100%', padding: '0.75rem', fontSize: '0.95rem',
                  border: '2px solid var(--gray-300)', borderRadius: 'var(--radius-sm)',
                  resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
                  outline: 'none', transition: 'border 0.3s',
                }}
                onFocus={(e) => e.target.style.borderColor = '#d63031'}
                onBlur={(e) => e.target.style.borderColor = 'var(--gray-300)'}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setRejectingPurchase(null); setRejectReason(''); }}
                style={{ flex: 1 }}
              >
                Cancelar
              </button>
              <button
                onClick={handleRejectConfirm}
                disabled={actionLoading || !rejectReason.trim()}
                style={{
                  flex: 1,
                  background: !rejectReason.trim() ? 'var(--gray-300)' : '#d63031',
                  color: 'white', border: 'none', fontWeight: 600,
                  padding: '0.75rem', borderRadius: 'var(--radius-sm)',
                  cursor: !rejectReason.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                }}
              >
                {actionLoading ? 'Rejeitando...' : '❌ Confirmar Rejeição'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Assign Modal */}
      {showManualAssign && (
        <div className="modal-overlay" onClick={() => setShowManualAssign(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>➕</span> Atribuir Números Manualmente
            </h2>

            <div style={{
              background: 'rgba(108, 92, 231, 0.08)',
              border: '1px solid rgba(108, 92, 231, 0.2)',
              borderRadius: 'var(--radius-sm)',
              padding: '1rem',
              marginBottom: '1.2rem',
              fontSize: '0.9rem',
              color: 'var(--primary)',
            }}>
              Use esta opção para corrigir problemas ou atribuir números manualmente. Os números serão marcados como confirmados sem passar pelo fluxo de pagamento.
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
                👤 Nome do Comprador *
              </label>
              <input
                type="text"
                value={manualBuyerName}
                onChange={(e) => setManualBuyerName(e.target.value)}
                placeholder="Nome completo"
                style={{
                  width: '100%', padding: '0.75rem', fontSize: '0.95rem',
                  border: '2px solid var(--gray-300)', borderRadius: 'var(--radius-sm)',
                  boxSizing: 'border-box', outline: 'none',
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
                📧 Email do Comprador *
              </label>
              <input
                type="email"
                value={manualBuyerEmail}
                onChange={(e) => setManualBuyerEmail(e.target.value)}
                placeholder="email@exemplo.com"
                style={{
                  width: '100%', padding: '0.75rem', fontSize: '0.95rem',
                  border: '2px solid var(--gray-300)', borderRadius: 'var(--radius-sm)',
                  boxSizing: 'border-box', outline: 'none',
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
                🔢 Números (separados por vírgula) *
              </label>
              <input
                type="text"
                value={manualNumbers}
                onChange={(e) => setManualNumbers(e.target.value)}
                placeholder="Ex: 5, 12, 33, 47"
                style={{
                  width: '100%', padding: '0.75rem', fontSize: '0.95rem',
                  border: '2px solid var(--gray-300)', borderRadius: 'var(--radius-sm)',
                  boxSizing: 'border-box', outline: 'none',
                }}
              />
              <small style={{ color: 'var(--dark-light)', fontSize: '0.8rem' }}>
                Os números já reservados/vendidos serão ignorados
              </small>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
                📝 Justificativa *
              </label>
              <textarea
                value={manualJustification}
                onChange={(e) => setManualJustification(e.target.value)}
                placeholder="Ex: Compra feita presencialmente, correção de problema no pagamento..."
                rows={2}
                style={{
                  width: '100%', padding: '0.75rem', fontSize: '0.95rem',
                  border: '2px solid var(--gray-300)', borderRadius: 'var(--radius-sm)',
                  boxSizing: 'border-box', outline: 'none', resize: 'vertical',
                  fontFamily: 'inherit',
                }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
                📎 Comprovante <span style={{ fontWeight: 400, color: 'var(--dark-light)', fontSize: '0.85rem' }}>(opcional)</span>
              </label>
              {!manualComprovante ? (
                <label style={{
                  display: 'block', padding: '1rem', border: '2px dashed var(--gray-300)',
                  borderRadius: 'var(--radius-sm)', background: 'var(--gray-100)',
                  cursor: 'pointer', textAlign: 'center', fontSize: '0.9rem',
                  color: 'var(--dark-light)', transition: 'all 0.3s',
                }}>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleManualComprovanteChange}
                    style={{ display: 'none' }}
                  />
                  📷 Clique para enviar comprovante
                </label>
              ) : (
                <div style={{
                  border: '2px solid var(--success)', borderRadius: 'var(--radius-sm)',
                  padding: '0.75rem', background: 'rgba(0,184,148,0.05)',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span>✅</span>
                    <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{manualComprovante.name}</span>
                  </div>
                  <button
                    onClick={() => { setManualComprovante(null); setManualComprovantePreview(null); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: '1.1rem' }}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowManualAssign(false); setManualComprovante(null); setManualComprovantePreview(null); }}
                style={{ flex: 1 }}
              >
                Cancelar
              </button>
              <button
                onClick={handleManualAssign}
                disabled={manualLoading || !manualBuyerName.trim() || !manualBuyerEmail.trim() || !manualNumbers.trim() || !manualJustification.trim()}
                style={{
                  flex: 1,
                  background: (!manualBuyerName.trim() || !manualBuyerEmail.trim() || !manualNumbers.trim() || !manualJustification.trim())
                    ? 'var(--gray-300)' : 'linear-gradient(135deg, #00b894, #00cec9)',
                  color: 'white', border: 'none', fontWeight: 600,
                  padding: '0.75rem', borderRadius: 'var(--radius-sm)',
                  cursor: (!manualBuyerName.trim() || !manualBuyerEmail.trim() || !manualNumbers.trim() || !manualJustification.trim())
                    ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                }}
              >
                {manualLoading ? 'Atribuindo...' : '✅ Atribuir Números'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Winner Selection Modal */}
      {showWinnerModal && !showWinnerConfirm && (
        <div className="modal-overlay" onClick={() => setShowWinnerModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>🏆</span> Definir Vencedor
            </h2>

            <div style={{
              background: 'linear-gradient(135deg, rgba(253,203,110,0.15), rgba(225,112,85,0.15))',
              borderRadius: 'var(--radius-sm)',
              padding: '1rem',
              marginBottom: '1.2rem',
              fontSize: '0.9rem',
              border: '1px solid #fdcb6e',
            }}>
              Selecione o número vencedor do sorteio. Essa ação é <strong>irreversível</strong>.
              O comprador será notificado por e-mail.
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
                🔢 Número Vencedor *
              </label>
              <select
                value={selectedWinnerNumber}
                onChange={(e) => setSelectedWinnerNumber(e.target.value)}
                style={{
                  width: '100%', padding: '0.75rem', fontSize: '1rem',
                  border: '2px solid var(--gray-300)', borderRadius: 'var(--radius-sm)',
                  boxSizing: 'border-box', outline: 'none', background: 'var(--white)',
                }}
              >
                <option value="">Selecione o número...</option>
                {confirmedTickets
                  .sort((a, b) => (a.number || 0) - (b.number || 0))
                  .map(t => (
                    <option key={t.id} value={t.number}>
                      Nº {t.number} — {t.buyerName || t.buyerEmail || 'Comprador'}
                    </option>
                  ))
                }
              </select>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
                📎 Comprovante do Sorteio <span style={{ fontWeight: 400, color: 'var(--dark-light)', fontSize: '0.85rem' }}>(ex: print da loteria)</span>
              </label>
              {!drawProofFile ? (
                <label style={{
                  display: 'block', padding: '1.2rem', border: '2px dashed var(--gray-300)',
                  borderRadius: 'var(--radius-sm)', background: 'var(--gray-100)',
                  cursor: 'pointer', textAlign: 'center', fontSize: '0.9rem',
                  color: 'var(--dark-light)',
                }}>
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleDrawProofChange}
                    style={{ display: 'none' }}
                  />
                  📷 Clique para enviar comprovante do sorteio
                  <div style={{ fontSize: '0.8rem', marginTop: '0.3rem' }}>Imagem ou PDF (máx 5MB)</div>
                </label>
              ) : (
                <div style={{
                  border: '2px solid var(--success)', borderRadius: 'var(--radius-sm)',
                  padding: '0.75rem', background: 'rgba(0,184,148,0.05)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>✅</span>
                      <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>{drawProofFile.name}</span>
                    </div>
                    <button onClick={() => { setDrawProofFile(null); setDrawProofPreview(null); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: '1.1rem' }}>
                      ✕
                    </button>
                  </div>
                  {drawProofPreview && drawProofFile.type?.startsWith('image/') && (
                    <img src={drawProofPreview} alt="Comprovante" style={{
                      width: '100%', maxHeight: '150px', objectFit: 'contain',
                      borderRadius: 'var(--radius-sm)', marginTop: '0.5rem'
                    }} />
                  )}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button className="btn btn-secondary" onClick={() => setShowWinnerModal(false)} style={{ flex: 1 }}>
                Cancelar
              </button>
              <button
                onClick={handleSelectWinner}
                disabled={!selectedWinnerNumber}
                style={{
                  flex: 1,
                  background: !selectedWinnerNumber ? 'var(--gray-300)' : 'linear-gradient(135deg, #fdcb6e, #e17055)',
                  color: 'white', border: 'none', fontWeight: 600,
                  padding: '0.75rem', borderRadius: 'var(--radius-sm)',
                  cursor: !selectedWinnerNumber ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                }}
              >
                Continuar →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Winner Confirmation Modal */}
      {showWinnerConfirm && winnerTicketInfo && (
        <div className="modal-overlay" onClick={() => setShowWinnerConfirm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px', textAlign: 'center' }}>
            <div style={{ fontSize: '4rem', marginBottom: '0.5rem' }}>🏆</div>
            <h2 style={{ margin: '0 0 1rem' }}>Confirmar Vencedor</h2>

            <div style={{
              background: 'linear-gradient(135deg, rgba(253,203,110,0.2), rgba(225,112,85,0.2))',
              borderRadius: 'var(--radius-lg)',
              padding: '1.5rem',
              marginBottom: '1.5rem',
            }}>
              <div style={{ fontSize: '2.5rem', fontWeight: 800, color: 'var(--primary)', marginBottom: '0.5rem' }}>
                Nº {winnerTicketInfo.number}
              </div>
              <div style={{ fontSize: '1.2rem', fontWeight: 700, color: 'var(--dark)' }}>
                {winnerTicketInfo.buyerName}
              </div>
              <div style={{ fontSize: '0.9rem', color: 'var(--dark-light)', marginTop: '0.25rem' }}>
                {winnerTicketInfo.buyerEmail}
              </div>
            </div>

            <div style={{
              background: 'rgba(214, 48, 49, 0.08)',
              border: '1px solid rgba(214, 48, 49, 0.2)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.75rem 1rem',
              marginBottom: '1.5rem',
              fontSize: '0.85rem',
              color: '#d63031',
              textAlign: 'left',
            }}>
              ⚠️ <strong>Atenção:</strong> Esta ação não pode ser desfeita. A rifa será encerrada e o vencedor será notificado por e-mail.
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowWinnerConfirm(false)}
                style={{ flex: 1 }}
              >
                ← Voltar
              </button>
              <button
                onClick={handleConfirmWinner}
                disabled={winnerLoading}
                style={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #fdcb6e, #e17055)',
                  color: 'white', border: 'none', fontWeight: 700,
                  padding: '0.75rem', borderRadius: 'var(--radius-sm)',
                  cursor: winnerLoading ? 'not-allowed' : 'pointer',
                  fontSize: '1rem',
                }}
              >
                {winnerLoading ? 'Finalizando...' : '🏆 Confirmar Vencedor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Number Modal */}
      {showRemoveNumbers && removeTicket && (
        <div className="modal-overlay" onClick={() => { setShowRemoveNumbers(false); setRemoveTicket(null); }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span>🗑️</span> Remover Número
            </h2>

            <div style={{
              background: 'rgba(214, 48, 49, 0.08)',
              border: '1px solid rgba(214, 48, 49, 0.2)',
              borderRadius: 'var(--radius-sm)',
              padding: '1rem',
              marginBottom: '1.2rem',
              fontSize: '0.9rem',
              color: '#d63031',
            }}>
              Você está removendo o número <strong>{removeTicket.number}</strong> de <strong>{removeTicket.buyerName || 'Comprador'}</strong> ({removeTicket.buyerEmail || '-'}).
              O número será liberado e ficará disponível novamente.
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem' }}>
                📝 Motivo da Remoção *
              </label>
              <textarea
                value={removeJustification}
                onChange={(e) => setRemoveJustification(e.target.value)}
                placeholder="Ex: Comprador solicitou cancelamento, erro na atribuição..."
                rows={3}
                style={{
                  width: '100%', padding: '0.75rem', fontSize: '0.95rem',
                  border: '2px solid var(--gray-300)', borderRadius: 'var(--radius-sm)',
                  resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
                  outline: 'none',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowRemoveNumbers(false); setRemoveTicket(null); setRemoveJustification(''); }}
                style={{ flex: 1 }}
              >
                Cancelar
              </button>
              <button
                onClick={handleRemoveNumber}
                disabled={removeLoading || !removeJustification.trim()}
                style={{
                  flex: 1,
                  background: !removeJustification.trim() ? 'var(--gray-300)' : '#d63031',
                  color: 'white', border: 'none', fontWeight: 600,
                  padding: '0.75rem', borderRadius: 'var(--radius-sm)',
                  cursor: !removeJustification.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                }}
              >
                {removeLoading ? 'Removendo...' : '🗑️ Confirmar Remoção'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Raffle Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000, padding: '1rem',
        }}>
          <div style={{
            background: 'white', borderRadius: 'var(--radius-lg)', padding: '2rem',
            maxWidth: '450px', width: '100%',
          }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>⚠️</div>
              <h3 style={{ margin: '0 0 0.5rem', color: '#d63031' }}>Excluir Rifa</h3>
              <p style={{ color: 'var(--dark-light)', margin: 0, lineHeight: 1.6 }}>
                Tem certeza que deseja excluir a rifa <strong>"{raffle.title}"</strong>? Esta ação não pode ser desfeita.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleteLoading}
                style={{
                  flex: 1, padding: '0.75rem', border: '1.5px solid var(--border)',
                  borderRadius: 'var(--radius-sm)', background: 'white', cursor: 'pointer',
                  fontWeight: 600, fontSize: '0.95rem',
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteRaffle}
                disabled={deleteLoading}
                style={{
                  flex: 1, padding: '0.75rem', border: 'none',
                  borderRadius: 'var(--radius-sm)', background: '#d63031', color: 'white',
                  cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem',
                }}
              >
                {deleteLoading ? 'Excluindo...' : '🗑️ Excluir Rifa'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Sub-components =====

function PurchaseCard({ purchase, currency, actionLoading, onConfirm, onReject, onViewComprovante, showActions }) {
  const isProcessing = actionLoading === purchase.id;
  const statusColors = {
    pending: '#f39c12',
    confirmed: '#00b894',
    rejected: '#d63031',
  };

  const createdAt = purchase.createdAt?.toDate
    ? purchase.createdAt.toDate().toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : purchase.createdAt || '-';

  return (
    <div style={{
      background: 'var(--white)',
      borderRadius: 'var(--radius-lg)',
      padding: '1.5rem',
      boxShadow: 'var(--shadow-md)',
      borderLeft: `4px solid ${statusColors[purchase.status] || 'var(--gray-300)'}`,
      opacity: isProcessing ? 0.6 : 1,
      transition: 'opacity 0.3s',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
        {/* Info */}
        <div style={{ flex: 1, minWidth: '200px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <strong style={{ fontSize: '1.05rem' }}>{purchase.buyerName || 'Comprador'}</strong>
            <StatusBadge status={purchase.status} />
          </div>

          <div style={{ fontSize: '0.9rem', color: 'var(--dark-light)', lineHeight: 1.8 }}>
            <div>📧 {purchase.buyerEmail || '-'}</div>
            <div>
              🎯 Números:{' '}
              {(purchase.selectedNumbers || []).map(n => (
                <span key={n} style={{
                  display: 'inline-block', background: 'var(--primary)', color: 'white',
                  padding: '2px 8px', borderRadius: '6px', fontWeight: 600, fontSize: '0.8rem', margin: '2px'
                }}>
                  {n}
                </span>
              ))}
            </div>
            <div>💰 {formatCurrency(purchase.totalAmount || 0, currency || 'BRL')}</div>
            <div>📅 {createdAt}</div>
          </div>

          {/* Reject reason */}
          {purchase.rejectReason && (
            <div style={{
              marginTop: '0.75rem',
              background: 'rgba(214, 48, 49, 0.06)',
              border: '1px solid rgba(214, 48, 49, 0.2)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.6rem 0.8rem',
              fontSize: '0.85rem',
              color: '#d63031',
            }}>
              <strong>Motivo:</strong> {purchase.rejectReason}
            </div>
          )}

          {/* Manual tag */}
          {purchase.paymentMethod === 'manual' && (
            <div style={{
              marginTop: '0.5rem',
              background: 'rgba(108, 92, 231, 0.08)',
              borderRadius: 'var(--radius-sm)',
              padding: '0.4rem 0.8rem',
              fontSize: '0.8rem',
              color: 'var(--primary)',
              fontWeight: 600,
              display: 'inline-block',
            }}>
              ➕ Atribuição manual
            </div>
          )}

          {/* Comprovante link */}
          {purchase.comprovanteUrl && (
            <button
              onClick={onViewComprovante}
              style={{
                marginTop: '0.75rem',
                background: 'linear-gradient(135deg, rgba(108,92,231,0.1), rgba(0,206,201,0.1))',
                border: '1px solid var(--primary)',
                borderRadius: 'var(--radius-sm)',
                padding: '0.5rem 1rem',
                cursor: 'pointer',
                fontWeight: 600,
                color: 'var(--primary)',
                fontSize: '0.9rem',
              }}
            >
              📎 Ver Comprovante
            </button>
          )}
        </div>

        {/* Actions */}
        {showActions && (
          <div style={{ display: 'flex', gap: '0.5rem', alignSelf: 'center' }}>
            <button
              onClick={onConfirm}
              disabled={isProcessing}
              style={{
                background: 'var(--success)', color: 'white', border: 'none',
                padding: '0.6rem 1.2rem', borderRadius: 'var(--radius-sm)',
                fontWeight: 600, cursor: isProcessing ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
              }}
            >
              {isProcessing ? '...' : '✅ Confirmar'}
            </button>
            <button
              onClick={onReject}
              disabled={isProcessing}
              style={{
                background: 'var(--danger)', color: 'white', border: 'none',
                padding: '0.6rem 1.2rem', borderRadius: 'var(--radius-sm)',
                fontWeight: 600, cursor: isProcessing ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
              }}
            >
              {isProcessing ? '...' : '❌ Rejeitar'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    pending: { bg: '#ffeaa7', color: '#2d3436', label: 'Pendente' },
    confirmed: { bg: '#00b894', color: 'white', label: 'Confirmado' },
    rejected: { bg: '#d63031', color: 'white', label: 'Rejeitado' },
  };
  const c = config[status] || { bg: 'var(--gray-200)', color: 'var(--dark)', label: status };

  return (
    <span style={{
      background: c.bg, color: c.color,
      padding: '3px 10px', borderRadius: '12px',
      fontSize: '0.75rem', fontWeight: 600,
    }}>
      {c.label}
    </span>
  );
}

function EmptyState({ icon, title, subtitle }) {
  return (
    <div style={{
      textAlign: 'center', padding: '3rem 1rem',
      background: 'var(--white)', borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-md)',
    }}>
      <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }}>{icon}</div>
      <h3 style={{ color: 'var(--dark)', margin: '0 0 0.5rem' }}>{title}</h3>
      {subtitle && <p style={{ color: 'var(--dark-light)', margin: 0 }}>{subtitle}</p>}
    </div>
  );
}
